import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import ReactGA from "react-ga";
import log from "loglevel";
import AuthLoginPage from "./pages/auth/AuthLoginPage";
import AuthTwoFactorRegistrationPage from "./pages/auth/AuthTwoFactorRegistrationPage";
import AuthTwoFactorVerificationPage from "./pages/auth/AuthTwoFactorVerificationPage";
import AuthOperationPage from "./pages/auth/AuthOperationPage";
import AuthResetPage from "./pages/auth/AuthResetPage";
import DashboardMainPage from "./pages/dashboard/DashboardMainPage";
import DashboardAccessControlPage from "./pages/dashboard/DashboardAccessControlPage";
import DashboardPermissionsPage from "./pages/dashboard/DashboardPermissionsPage";
import DashboardConfigPage from "./pages/dashboard/DashboardConfigPage";
import UserSettingsPage from "./pages/settings/UserSettingsPage";
import AccountsPage from "./pages/accounts/AccountsPage";
import AccountPage from "./pages/accounts/account/AccountPage";
import AccountTeamPage from "./pages/accounts/account/team/AccountTeamPage";
import AccountAnalysisPage from "./pages/accounts/account/analysis/AccountAnalysisPage";
import DocumentationPage from "./pages/utility/DocumentationPage";
import NotFoundPage from "./pages/utility/NotFoundPage";
import AboutPage from "./pages/about/AboutPage";
import {
  isBasicVerified,
  isTwoFactorRegistered,
  isTwoFactorVerified,
  isEmailVerifying,
  isEmailVerifyingBasicAuth,
} from "services";
import { isAuthed } from "lib/auth";
import styles from "./App.module.scss";
import * as routes from "routes";
import { getDomainName } from "lib/domain";
import { gaTrackingCode } from "settings";

const initializeReactGA = () => {
  gaTrackingCode
    ? ReactGA.initialize(gaTrackingCode)
    : log.warn(
        "GoogleAnalytics Tracking Code not provided. Will not initialize."
      );
};

initializeReactGA();

const App: React.FC = () => {
  ReactGA.pageview(window.location.pathname);
  return (
    <div className={styles["App"]}>
      <Router>
        <Switch>
          ${/* Auth */}
          <Route exact path={routes.ROOT} component={AuthLoginPage} />
          <Route exact path={routes.LOGIN} component={AuthLoginPage} />
          <Route
            path={routes.FORGOTTEN_PASSWORD}
            component={() => <AuthOperationPage query={"password"} />}
          />
          <Route
            path={routes.FORGOTTEN_PHONE}
            component={() => <AuthOperationPage query={"phone"} />}
          />
          <Route
            path={routes.RESEND_VERIFICICATION}
            component={() => (
              <AuthOperationPage query={"resend-verification"} />
            )}
          />
          <Route
            exact
            path={`${routes.VERIFY_EMAIL}/:verificationToken`}
            render={(props) => (
              <AuthResetPage
                authed={isAuthed(
                  isEmailVerifying(props.match.params.verificationToken)
                )}
              />
            )}
          />
          <Route
            path={`${routes.RESET_PASSWORD}/:verificationToken`}
            render={(props) => (
              <AuthResetPage
                authed={isAuthed(
                  isEmailVerifying(props.match.params.verificationToken)
                )}
              />
            )}
          />
          <Route
            path={`${routes.RESET_PHONE}/:verificationToken`}
            render={(props) => (
              <AuthResetPage
                authed={isAuthed(
                  isEmailVerifyingBasicAuth(
                    props.match.params.verificationToken
                  )
                )}
              />
            )}
          />
          <Route
            path={routes.TWOFACTOR_REGISTER}
            render={() => (
              <AuthTwoFactorRegistrationPage
                authed={isAuthed(isBasicVerified())}
                tfaRegistered={isAuthed(isTwoFactorVerified(), false)}
                tfaVerified={isAuthed(isTwoFactorVerified(), false)}
              />
            )}
          />
          <Route
            path={routes.TWOFACTOR_VERIFY}
            render={() => (
              <AuthTwoFactorVerificationPage
                authed={isAuthed(isBasicVerified())}
                tfaRegistered={isAuthed(isTwoFactorRegistered())}
                tfaVerified={isAuthed(isTwoFactorVerified(), false)}
              />
            )}
          />
          {/* Dashboard */}
          <Route
            exact
            path={routes.DASHBOARD_MAIN}
            render={() => (
              <DashboardMainPage authed={isAuthed(isTwoFactorVerified())} />
            )}
          />
          <Route
            exact
            path={routes.DASHBOARD_ADMIN}
            render={() => <Redirect to={routes.DASHBOARD_CONFIGURATION} />}
          />
          <Route
            path={routes.DASHBOARD_ACCESS_CONTROL}
            render={() => (
              <DashboardAccessControlPage
                authed={isAuthed(isTwoFactorVerified())}
              />
            )}
          />
          <Route
            path={routes.DASHBOARD_ROLES_PERMISSIONS}
            render={() => (
              <DashboardPermissionsPage
                authed={isAuthed(isTwoFactorVerified())}
              />
            )}
          />
          <Route
            path={routes.DASHBOARD_CONFIGURATION}
            render={() => (
              <DashboardConfigPage authed={isAuthed(isTwoFactorVerified())} />
            )}
          />
          {/* Tech Debt: This should be a part of PortSight-Admin-Client */}
          ${/* User Settings */}
          <Route
            path={routes.SETTINGS}
            render={() => (
              <UserSettingsPage authed={isAuthed(isTwoFactorVerified())} />
            )}
          />
          ${/* About Page */}
          <Route path={routes.ABOUT} render={() => <AboutPage />} />
          {/* Account View */}
          <Route
            exact
            path={routes.ACCOUNTS}
            render={() => (
              <AccountsPage authed={isAuthed(isTwoFactorVerified())} />
            )}
          />
          <Route
            exact
            path={routes.ACCOUNT(":accountName")}
            render={(props) => (
              <AccountPage
                accountName={props.match.params.accountName}
                authed={isAuthed(isTwoFactorVerified())}
              />
            )}
          />
          <Route
            exact
            path={routes.ACCOUNT_TEAM(":accountName", ":teamName")}
            render={(props) => (
              <AccountTeamPage
                accountName={props.match.params.accountName}
                teamName={props.match.params.teamName}
                authed={isAuthed(isTwoFactorVerified())}
              />
            )}
          />
          <Route
            exact
            path={routes.ACCOUNT_ANALYSIS(":accountName", ":analysisName")}
            render={(props) => (
              <AccountAnalysisPage
                accountName={props.match.params.accountName}
                analysisName={props.match.params.analysisName}
                authed={isAuthed(isTwoFactorVerified())}
              />
            )}
          />
          {/* External Apps */}
          <Route
            exact
            path={routes.AICON_DUMMY_ROUTE}
            render={() => {
              const domain = getDomainName();
              window.location.href = `${domain}${routes.AICON_APP_ROOT}`;
              return null;
            }}
          />
          {/* Utility */}
          <Route
            path={routes.DOCUMENTATION}
            render={() => (
              <DocumentationPage authed={isAuthed(isTwoFactorVerified())} />
            )}
          />
          <Route path="*" exact component={NotFoundPage} />
          {/* Redirects */}
          <Redirect from="/tfa" to={routes.TWOFACTOR_VERIFY} />
        </Switch>
      </Router>
    </div>
  );
};

export default App;
